import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, Camera, X, File as FileIcon, Image } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDecimal } from "@/lib/formatters";

const MAX_IMAGE_DIM = 1920;
const IMAGE_QUALITY = 0.85;

export interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  type: 'image' | 'document';
  /** Server-Foto-ID (numerisch). Gesetzt sobald das Bild persistent gespeichert ist. */
  serverPhotoId?: number;
  /** Optionale Anzeigegröße — verwendet, wenn `file` ein Stub ohne Inhalt ist. */
  displaySize?: number;
}

interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  title: string;
  description: string;
  icon?: React.ReactNode;
  onFilesChange?: (files: UploadedFile[]) => void;
  allowCamera?: boolean;
  initialFiles?: UploadedFile[];
  /**
   * Wenn gesetzt, übernimmt der Parent das Löschen (Bestätigungsdialog +
   * Server-DELETE + lokales Aufräumen). Der interne `removeFile` ändert dann
   * die Liste NICHT mehr selbst — der Parent entscheidet, wann/ob die Datei
   * aus `initialFiles`/State entfernt wird. Das verhindert implizite Löschungen
   * via Listen-Diff in `onFilesChange`.
   */
  onDeleteRequest?: (fileId: string) => void;
}

async function resizeImageFile(file: File): Promise<{ file: File; preview: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new window.Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_IMAGE_DIM || height > MAX_IMAGE_DIM) {
          if (width >= height) {
            height = Math.round((height * MAX_IMAGE_DIM) / width);
            width = MAX_IMAGE_DIM;
          } else {
            width = Math.round((width * MAX_IMAGE_DIM) / height);
            height = MAX_IMAGE_DIM;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", IMAGE_QUALITY);
        const arr = dataUrl.split(",");
        const bstr = atob(arr[1]);
        const u8arr = new Uint8Array(bstr.length);
        for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
        const blob = new Blob([u8arr], { type: "image/jpeg" });
        const baseName = file.name.replace(/\.[^/.]+$/, "");
        const resizedFile = new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
        resolve({ file: resizedFile, preview: dataUrl });
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function FileUpload({
  accept = "*/*",
  multiple = true,
  maxSize = 10,
  title,
  description,
  icon,
  onFilesChange,
  allowCamera = false,
  initialFiles,
  onDeleteRequest,
}: FileUploadProps) {
  // CRITICAL: `files` MUST be lazy-initialized from `initialFiles` on the very
  // first render. Wenn wir mit `[]` starten und erst per useEffect auf
  // initialFiles setzen, feuert der „notify parent"-Effect (siehe unten) einmal
  // mit `[]` BEVOR die Restore-Liste angekommen ist. Ein Parent, der daraus
  // „Fotos wurden entfernt" ableitet (handlePhotosUploaded in project-edit),
  // löscht dann alle bereits persistierten Fotos serverseitig — der echte
  // Production-Bug, bei dem nach Re-Mount alle Bilder verschwanden.
  const [files, setFiles] = useState<UploadedFile[]>(() => initialFiles ?? []);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const onFilesChangeRef = useRef(onFilesChange);
  useEffect(() => { onFilesChangeRef.current = onFilesChange; });

  // Wenn `initialFiles` NACH dem Mount erstmals gesetzt wird (z.B. Hydration
  // kommt nach dem ersten Render), die Liste einmalig nachziehen. Wir prüfen
  // weiterhin per Ref, dass das nur einmal passiert, damit spätere Änderungen
  // an `initialFiles` keine User-Edits zurücksetzen.
  const initialFilesLoadedRef = useRef<boolean>(
    Array.isArray(initialFiles) && initialFiles.length > 0,
  );
  useEffect(() => {
    if (!initialFilesLoadedRef.current && initialFiles && initialFiles.length > 0) {
      initialFilesLoadedRef.current = true;
      setFiles(initialFiles);
    }
  }, [initialFiles]);

  const acceptsImages = accept.includes("image");

  const processFiles = useCallback(async (fileList: FileList) => {
    const newFiles: UploadedFile[] = [];

    for (const file of Array.from(fileList)) {
      const isImage = file.type.startsWith("image/");
      const fileId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      if (isImage) {
        try {
          const { file: resizedFile, preview } = await resizeImageFile(file);
          newFiles.push({ id: fileId, file: resizedFile, preview, type: "image" });
        } catch {
          const reader = new FileReader();
          const preview = await new Promise<string>((res) => {
            reader.onload = (e) => res(e.target?.result as string);
            reader.readAsDataURL(file);
          });
          newFiles.push({ id: fileId, file, preview, type: "image" });
        }
      } else {
        if (file.size > maxSize * 1024 * 1024) {
          alert(`Datei ${file.name} ist zu groß. Maximum: ${maxSize}MB`);
          continue;
        }
        newFiles.push({ id: fileId, file, type: "document" });
      }
    }

    if (newFiles.length === 0) return;

    if (multiple) {
      setFiles((prev) => [...prev, ...newFiles]);
    } else {
      setFiles(newFiles);
    }
  }, [maxSize, multiple]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileSelect = useCallback(() => { fileInputRef.current?.click(); }, []);
  const handleCameraCapture = useCallback(() => { cameraInputRef.current?.click(); }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = "";
    }
  }, [processFiles]);

  const removeFile = useCallback((fileId: string) => {
    // Wenn der Parent das Löschen übernimmt (onDeleteRequest gesetzt), NICHT
    // selbst die files-Liste verändern. Sonst würde `onFilesChange` mit der
    // verkleinerten Liste feuern und der Parent könnte das als implizites
    // Löschen interpretieren — genau die Bug-Klasse, die wir verhindern wollen.
    if (onDeleteRequest) {
      onDeleteRequest(fileId);
      return;
    }
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, [onDeleteRequest]);

  // Notify parent whenever the file list changes (uploads or removals).
  // prevFilesRef wird mit demselben Initial-Wert wie `files` geseedet, damit
  // beim ersten Effect-Run nach Mount KEINE Phantom-Notification rausgeht
  // (sonst würde der Parent ein „Reset auf initialFiles" als User-Aktion
  // missdeuten — siehe Lazy-Init oben).
  const prevFilesRef = useRef<UploadedFile[]>(files);
  const initialNotifySkippedRef = useRef(false);
  useEffect(() => {
    if (!initialNotifySkippedRef.current) {
      initialNotifySkippedRef.current = true;
      prevFilesRef.current = files;
      return;
    }
    if (files === prevFilesRef.current) return;
    prevFilesRef.current = files;
    onFilesChangeRef.current?.(files);
  }, [files]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return formatDecimal(bytes / Math.pow(k, i), 2, { minFractionDigits: 0 }) + " " + sizes[i];
  };

  return (
    <div className="space-y-4">
      <Card
        className={cn(
          "border-2 border-dashed transition-colors cursor-pointer",
          isDragOver ? "border-bauwerk-orange bg-orange-50" : "border-gray-300 hover:border-gray-400"
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleFileSelect}
      >
        <CardContent className="p-4 text-center">
          <div className="mb-2">
            {icon || <Upload className="w-8 h-8 text-gray-400 mx-auto" />}
          </div>
          <h3 className="font-medium text-gray-700 mb-1 text-sm">{title}</h3>
          <p className="text-gray-600 mb-3 text-xs">{description}</p>
          <div className="flex justify-center space-x-2">
            <Button variant="outline" size="sm" type="button" className="text-xs px-2 py-1">
              <Upload className="w-3 h-3 mr-1" />
              Auswählen
            </Button>
            {allowCamera && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                className="text-xs px-2 py-1"
                onClick={(e) => { e.stopPropagation(); handleCameraCapture(); }}
              >
                <Camera className="w-3 h-3 mr-1" />
                Foto
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {acceptsImages ? "Fotos werden automatisch komprimiert" : `Max. ${maxSize}MB`}
          </p>
        </CardContent>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        className="hidden"
      />
      {allowCamera && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700">Hochgeladene Dateien:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {files.map((file) => (
              <Card key={file.id} className="p-3">
                <div className="flex items-center space-x-3">
                  {file.type === "image" ? (
                    <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                      {file.preview ? (
                        <img
                          src={file.preview}
                          alt={file.file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <Image className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                      <FileIcon className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">
                      {file.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.displaySize ?? file.file.size)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(file.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
